import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import './ChatScreen.css';
import { supChatbotMessages } from '../../api/supportAdmin';
import socket, { connectSocket } from '../../utils/socket';

export default function ChatScreen({ chatId, sendMessage, updateUserData, user }) {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const chatBodyRef = useRef(null);
    const bottomRef = useRef(null);
    const loadingRef = useRef(false);
    const initialScrollFrameRef = useRef(null);

    // Anchor used to restore scroll position after prepending older messages.
    // { id, offset } -> offset is the anchor message's distance (px) from the
    // top of the visible viewport at the moment we captured it.
    const anchorRef = useRef(null);

    // ---------------- Socket ----------------

    useEffect(() => {
        const handleConnect = () => {
            console.log('Socket Connected:', socket.id);
        };

        socket.on('connect', handleConnect);
        connectSocket();

        return () => {
            socket.off('connect', handleConnect);
        };
    }, []);

    useEffect(() => {
        const handleNewMessage = (data) => {
            if (data.chatId && data.chatId !== chatId) return;

            const newMessage = data.message || data;

            setMessages((prev) => [...prev, newMessage]);

            updateUserData({
                updates: {
                    unreadCount: 0
                }
            });

            requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView({
                    behavior: 'smooth'
                });
            });
        };

        const handleStatusUpdate = (data) => {
            setMessages((prev) =>
                prev.map((msg) => (msg._id === data.messageId ? { ...msg, status: data.status } : msg))
            );
        };

        socket.on('new-message', handleNewMessage);
        socket.on('message-status-updated', handleStatusUpdate);

        return () => {
            socket.off('new-message', handleNewMessage);
            socket.off('message-status-updated', handleStatusUpdate);
        };
    }, [chatId]);

    // ---------------- Initial Load ----------------

    useEffect(() => {
        if (!chatId) return;

        setMessages([]);
        setHasMore(true);

        loadMessages();

        updateUserData({
            updates: {
                unreadCount: 0
            }
        });

        return () => {
            if (initialScrollFrameRef.current) {
                cancelAnimationFrame(initialScrollFrameRef.current);
                initialScrollFrameRef.current = null;
            }
        };
    }, [chatId]);

    // ---------------- Restore scroll position after older messages load ----------------

    useLayoutEffect(() => {
        if (!anchorRef.current) return;

        const container = chatBodyRef.current;
        const { id, offset } = anchorRef.current;

        const anchorEl = container.querySelector(`[data-msg-id="${id}"]`);

        if (anchorEl) {
            // Put the anchor message back at the exact same position it was
            // at on screen before older messages were prepended.
            container.scrollTop = anchorEl.offsetTop - offset;
        }

        anchorRef.current = null;
    }, [messages]);

    const loadMessages = async () => {
        try {
            const res = await supChatbotMessages(chatId);

            setMessages(res.data);

            setHasMore(res.hasMore);

            initialScrollFrameRef.current = requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView();
                initialScrollFrameRef.current = null;
            });
        } catch (err) {
            console.log(err);
        }
    };

    // ---------------- Load Older Messages ----------------

    const loadOlderMessages = async () => {
        if (loadingRef.current || !hasMore || messages.length === 0) return;

        if (initialScrollFrameRef.current) {
            cancelAnimationFrame(initialScrollFrameRef.current);
            initialScrollFrameRef.current = null;
        }

        loadingRef.current = true;
        setLoading(true);

        // Capture the anchor: whichever message is currently at (or just
        // above) the top of the visible viewport, and its distance from
        // the top edge of the container at this moment.
        const container = chatBodyRef.current;

        if (container) {
            const messageEls = container.querySelectorAll('[data-msg-id]');
            let anchorEl = null;

            for (const el of messageEls) {
                if (el.offsetTop >= container.scrollTop) {
                    anchorEl = el;
                    break;
                }
            }

            if (anchorEl) {
                anchorRef.current = {
                    id: anchorEl.getAttribute('data-msg-id'),
                    offset: anchorEl.offsetTop - container.scrollTop
                };
            }
        }

        try {
            const oldestId = messages[0]._id;

            const res = await supChatbotMessages(chatId, oldestId, 10);

            if (res.data.length > 0) {
                setMessages((prev) => [...res.data, ...prev]);

                setHasMore(res.hasMore);
            } else {
                anchorRef.current = null;
                setHasMore(false);
            }
        } catch (err) {
            anchorRef.current = null;
            console.log(err);
        }

        loadingRef.current = false;
        setLoading(false);
    };

    // ---------------- Scroll ----------------

    const handleScroll = (e) => {
        if (loadingRef.current) return;

        if (e.target.scrollTop <= 40) {
            loadOlderMessages();
        }
    };

    // ---------------- Send ----------------

    const handleSend = async () => {
        if (!message.trim() || sending) return;

        setSending(true);

        try {
            await sendMessage(message);

            setMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className='chat-container'>
            <div className='chat-header'>
                <span>Conversation</span>
                <div className='csToggleWrap' onClick={(e) => e.stopPropagation()}>
                    <span className='csToggleLabel'>Bot Automation</span>
                    <label className='csToggle'>
                        <input
                            type='checkbox'
                            checked={!user?.isAgentHandling}
                            onChange={(e) => {
                                e.stopPropagation();
                                updateUserData({
                                    updates: {
                                        isAgentHandling: !e.target.checked
                                    }
                                });
                            }}
                        />
                        <span className='csToggleTrack' />
                        <span className='csToggleThumb' />
                    </label>
                    {/* <span className={data.createDate ? 'toggle-status on' : 'toggle-status off'}>
                                    {data.createDate ? 'On' : 'Off'}
                                </span> */}
                </div>
            </div>

            <div className='chat-body' ref={chatBodyRef} onScroll={handleScroll}>
                {loading && (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '10px',
                            color: '#888'
                        }}
                    >
                        Loading...
                    </div>
                )}

                {messages.map((msg, index) => {
                    const incoming = msg.direction === 'incoming';

                    const currentDate = moment(msg.createdAt);

                    const previousDate = index > 0 ? moment(messages[index - 1].createdAt) : null;

                    const isNewDate = !previousDate || !currentDate.isSame(previousDate, 'day');

                    let dateLabel = '';

                    if (isNewDate) {
                        if (currentDate.isSame(moment(), 'day')) {
                            dateLabel = 'TODAY';
                        } else if (currentDate.isSame(moment().subtract(1, 'day'), 'day')) {
                            dateLabel = 'YESTERDAY';
                        } else {
                            dateLabel = currentDate.format('MMMM D, YYYY');
                        }
                    }

                    return (
                        <div key={msg._id}>
                            {isNewDate && (
                                <div className='chat-date-separator'>
                                    <span>{dateLabel}</span>
                                </div>
                            )}

                            <div data-msg-id={msg._id} className={`message-row ${incoming ? 'left' : 'right'}`}>
                                <div className={`message ${incoming ? 'incoming' : 'outgoing'}`}>
                                    {msg.text}

                                    <div className='message-footer'>
                                        <span>{currentDate.format('hh:mm A')}</span>

                                        {!incoming && (
                                            <span className='status'>
                                                {msg.status === 'sent' && '✓'}
                                                {msg.status === 'delivered' && '✓✓'}
                                                {msg.status === 'read' && <span className='read'>✓✓</span>}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div ref={bottomRef} />
            </div>

            <div className='chat-input'>
                <textarea
                    placeholder='Type a message...'
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />

                <button onClick={handleSend} disabled={sending}>
                    {sending ? 'Sending...' : 'Send'}
                </button>
            </div>
        </div>
    );
}
